import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { useNotifications } from '../../hooks/useNotifications';
import { logout } from '../../services/authService';
import { formatPostDate } from '../../utils/dateFormatter';
import Button from '../ui/Button';
import Avatar from '../ui/Avatar';
import PremiumName from '../ui/PremiumName';
import { db } from '../../firebase';
import { collection, query, limit, getDocs } from 'firebase/firestore';
import { searchPosts } from '../../services/postService';
import { allWordsPresent } from '../../utils/searchUtils';
import { PostSuggestion } from '../search';
import { isAdminUser } from '../../constants/admin';

// AudioContext compartido; desbloqueado tras primer gesto del usuario (política autoplay)
let sharedAudioContext = null;
let lastNotificationSoundTime = 0;
const NOTIFICATION_SOUND_THROTTLE_MS = 3000;
const NOTIFICATION_SOUND_DEBOUNCE_MS = 400;

function getOrCreateAudioContext() {
  if (sharedAudioContext) return sharedAudioContext;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  sharedAudioContext = new Ctx();
  return sharedAudioContext;
}

function playNotificationSound() {
  try {
    const ctx = getOrCreateAudioContext();
    if (!ctx || ctx.state === 'suspended') return;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.15);
  } catch {
    // Ignorar si no permitido (p. ej. sin gesto previo)
  }
}

const NOTIFICATION_LABELS = {
  like: (p) => `${p.actorUserName || 'Alguien'} le gustó tu publicación`,
  comment: (p) => `${p.actorUserName || 'Alguien'} comentó en tu publicación`,
  order_request: (p) => `${p.clientName || 'Un cliente'} solicitó tu servicio: ${(p.serviceName || '').slice(0, 30)}...`,
  order_accepted: (p) => `${p.sellerName || 'El vendedor'} aceptó tu solicitud`,
  order_rejected: (p) => `${p.sellerName || 'El vendedor'} rechazó tu solicitud`,
  order_payment_requested: (p) => `${p.sellerName || 'El vendedor'} solicitó el pago de tu servicio`,
  order_in_progress: (p) => `${p.sellerName || 'El vendedor'} comenzó a trabajar en tu pedido`,
  order_completed: (p) => `${p.sellerName || 'El vendedor'} marcó tu pedido como completado`,
  order_deliverables_added: (p) => `${p.sellerName || 'El vendedor'} envió ${p.fileCount ?? 0} archivo(s) para tu pedido`,
  new_follower: (p) => `${p.followerName || 'Alguien'} te siguió`,
  order_message: (p) => `Chat de ${(p.serviceName || 'servicio').slice(0, 40)}${p.serviceName && p.serviceName.length > 40 ? '...' : ''} (${p.messageCount || 1})`
};

/**
 * Header moderno con navegación, search, toggle de tema y perfil de usuario
 */
const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userData } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { notifications, unreadCount, markAsRead, markAllAsRead, refresh } = useNotifications(currentUser?.uid);
  const recentNotifications = (notifications || []).slice(0, 10);
  const prevUnreadRef = useRef(undefined);
  const soundDebounceTimerRef = useRef(null);
  const [bellAnimating, setBellAnimating] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const searchQueryRef = useRef(searchQuery);
  const [suggestions, setSuggestions] = useState({ users: [], items: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  
  searchQueryRef.current = searchQuery;
  const blockedIds = userData?.blocked || [];
  const isAdmin = isAdminUser(currentUser);

  useEffect(() => {
    const unlock = () => {
      const ctx = getOrCreateAudioContext();
      if (ctx && ctx.state === 'suspended') ctx.resume();
    };
    const events = ['click', 'touchstart', 'keydown'];
    events.forEach((e) => document.addEventListener(e, unlock, { once: true, passive: true }));
    return () => events.forEach((e) => document.removeEventListener(e, unlock));
  }, []);

  useEffect(() => {
    const prev = prevUnreadRef.current;
    prevUnreadRef.current = unreadCount;
    if (prev === undefined) return;
    if (unreadCount <= prev) return;

    setBellAnimating(true);
    const t = setTimeout(() => setBellAnimating(false), 700);

    if (soundDebounceTimerRef.current) clearTimeout(soundDebounceTimerRef.current);
    soundDebounceTimerRef.current = setTimeout(() => {
      soundDebounceTimerRef.current = null;
      const now = Date.now();
      if (now - lastNotificationSoundTime >= NOTIFICATION_SOUND_THROTTLE_MS) {
        lastNotificationSoundTime = now;
        playNotificationSound();
      }
    }, NOTIFICATION_SOUND_DEBOUNCE_MS);

    return () => {
      clearTimeout(t);
      if (soundDebounceTimerRef.current) clearTimeout(soundDebounceTimerRef.current);
    };
  }, [unreadCount]);

  // Buscar sugerencias mientras escribe
  const fetchSuggestions = useCallback(async (searchTerm) => {
    if (!searchTerm.trim()) {
      setSuggestions({ users: [], items: [] });
      setShowSuggestions(false);
      return;
    }

    try {
      const firstWord = (searchTerm || '').trim().toLowerCase().split(/\s+/)[0] || '';

      // Buscar usuarios
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(query(usersRef, limit(80)));
      const foundUsers = usersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => {
          const username = user.username || '';
          const displayName = user.displayName || '';
          const email = user.email || '';
          const combined = `${username} ${displayName} ${email}`;
          return allWordsPresent(combined, searchTerm);
        })
        .sort((a, b) => {
          const aName = (a.username || a.displayName || '').toLowerCase();
          const bName = (b.username || b.displayName || '').toLowerCase();
          const aStarts = aName.startsWith(firstWord);
          const bStarts = bName.startsWith(firstWord);
          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          return 0;
        })
        .slice(0, 5);

      // Buscar posts
      const result = await searchPosts(searchTerm, 5);
      const foundItems = result.success ? (result.posts || []) : [];

      if (searchQueryRef.current !== searchTerm) return;
      setSuggestions({ users: foundUsers, items: foundItems });
      setShowSuggestions(true);
    } catch (error) {
      if (searchQueryRef.current !== searchTerm) return;
      console.error('Error al buscar sugerencias:', error);
    }
  }, []);

  // Navegar a la página de búsqueda
  const goToSearchPage = () => {
    const term = (searchQuery || '').trim();
    if (term) {
      setShowSuggestions(false);
      navigate(`/explore?q=${encodeURIComponent(term)}`);
    }
  };

  // Efecto para actualizar sugerencias (debounced 200ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        fetchSuggestions(searchQuery);
      } else {
        setSuggestions({ users: [], items: [] });
        setShowSuggestions(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [searchQuery, fetchSuggestions]);

  // Si el usuario está en Explorar, actualizar el query param en vivo (debounced)
  useEffect(() => {
    if (location.pathname !== '/explore') return;
    const t = setTimeout(() => {
      const term = (searchQuery || '').trim();
      if (term) {
        navigate(`/explore?q=${encodeURIComponent(term)}`, { replace: true });
      } else {
        navigate('/explore', { replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, location.pathname, navigate]);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSuggestions && !event.target.closest('.search-container')) {
        setShowSuggestions(false);
      }
      if (showCreateMenu && !event.target.closest('.create-menu-container')) {
        setShowCreateMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSuggestions, showCreateMenu]);

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      window.location.href = '/login';
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-200 dark:border-slate-700 shadow-sm">
      <nav className="container-custom">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group flex-shrink-0">
            <img 
              src="/logo.png" 
              alt="Illustra" 
              className="w-10 h-10 rounded-xl"
            />
            <div className="hidden sm:flex flex-col">
              <span className="text-2xl font-bold text-gradient">
                Illustra
              </span>
              <span
                className={`text-sm -mt-1 tracking-wide ${
                  userData?.isPremium
                    ? 'text-amber-500 dark:text-amber-400 font-semibold'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {userData?.isPremium ? 'Premium' : 'Para creativos'}
              </span>
            </div>
          </Link>

          {/* Navegación principal (desktop) */}
          <div className="hidden md:flex items-center gap-6 flex-shrink-0">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/'
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Inicio
            </Link>
            <Link
              to="/explore"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/explore'
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Explorar
            </Link>
            <Link
              to="/services"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/services'
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Servicios
            </Link>
            <Link
              to="/orders"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/orders'
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Mis Órdenes
            </Link>
            <Link
              to="/series"
              className={`text-sm font-medium transition-colors ${
                location.pathname.startsWith('/series')
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Series
            </Link>
            <Link
              to="/premium"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/premium'
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Premium
            </Link>
            <Link
              to="/adoptables"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/adoptables'
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Adoptables
            </Link>
          </div>

          {/* Search Bar */}
          <div className="relative flex-1 max-w-md search-container">
            <input
              type="text"
              placeholder="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  goToSearchPage();
                } else if (e.key === 'Escape') {
                  setSearchQuery('');
                  setShowSuggestions(false);
                }
              }}
              onFocus={() => {
                if (searchQuery.trim() && suggestions.users.length + suggestions.items.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              className="w-full pl-10 pr-10 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            />
            <svg 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setShowSuggestions(false);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}

            {/* Dropdown de sugerencias */}
            {showSuggestions && (suggestions.users.filter((u) => !blockedIds.includes(u.id)).length > 0 || suggestions.items.length > 0) && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl max-h-96 overflow-y-auto z-50">
                {/* Sugerencias de usuarios */}
                {suggestions.users.filter((u) => !blockedIds.includes(u.id)).length > 0 && (
                  <div className="p-2">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2">Usuarios</p>
                    {suggestions.users.filter((u) => !blockedIds.includes(u.id)).map(user => (
                      <Link
                        key={user.id}
                        to={`/profile/${user.id}`}
                        onClick={() => {
                          setShowSuggestions(false);
                          setSearchQuery('');
                        }}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      >
                        <Avatar
                          src={user.photoURL || user.profileImage}
                          alt={user.username || user.displayName}
                          fallbackText={user.username || user.displayName}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0">
                          <PremiumName
                            name={user.username || user.displayName || 'Usuario'}
                            isPremium={user.isPremium}
                            premiumColor={user.premiumNameColor}
                            nameClassName="text-sm font-medium text-gray-900 dark:text-white truncate block"
                          />
                          {user.profession && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {user.profession}
                            </p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Sugerencias de posts */}
                {suggestions.items.length > 0 && (
                  <div className="p-2 border-t border-gray-200 dark:border-slate-700">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2">Posts</p>
                    {suggestions.items.map(item => (
                      <PostSuggestion
                        key={item.id}
                        post={item}
                        onClick={() => {
                          setShowSuggestions(false);
                          setSearchQuery('');
                        }}
                      />
                    ))}
                  </div>
                )}

                {/* Botón para ver todos los resultados */}
                <div className="p-2 border-t border-gray-200 dark:border-slate-700">
                  <button
                    onClick={goToSearchPage}
                    className="w-full px-3 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors font-medium"
                  >
                    Ver todos los resultados
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Acciones */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Botón Crear (dropdown) */}
            {currentUser && userData && (
              <div className="relative create-menu-container hidden sm:block">
                <button
                  onClick={() => setShowCreateMenu(!showCreateMenu)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium text-sm transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Crear</span>
                </button>
                
                {showCreateMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl py-1 z-50">
                    <button
                      onClick={() => {
                        setShowCreateMenu(false);
                        navigate('/', { state: { openCreateModal: true } });
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="font-medium">Post</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateMenu(false);
                        navigate('/series?create=series');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4h10a2 2 0 012 2v14l-7-4-7 4V6a2 2 0 012-2z" />
                      </svg>
                      <span className="font-medium">Serie</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateMenu(false);
                        navigate('/series?create=episode');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h10" />
                      </svg>
                      <span className="font-medium">Episodio</span>
                    </button>
                    <Link
                      to={`/profile/${userData.id}?tab=servicios&create=true`}
                      onClick={() => setShowCreateMenu(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="font-medium">Servicio</span>
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Toggle de Tema */}
            <button
              onClick={toggleTheme}
              className="
                p-2.5 rounded-lg 
                text-gray-600 dark:text-gray-400 
                hover:bg-gray-100 dark:hover:bg-slate-800 
                transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
              "
              aria-label={`Cambiar a modo ${theme === 'light' ? 'oscuro' : 'claro'}`}
            >
              {theme === 'light' ? (
                // Icono de Luna (cambiar a modo oscuro)
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                // Icono de Sol (cambiar a modo claro)
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
            </button>

            {/* Notificaciones */}
            {currentUser && userData && (
              <Menu as="div" className="relative">
                <MenuButton
                  onClick={() => { if (unreadCount > 0) markAllAsRead(); }}
                  className={`relative p-2.5 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${bellAnimating ? 'animate-bell-notify' : ''}`}
                  aria-label="Notificaciones"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-[10px] font-bold text-white">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </MenuButton>
                <MenuItems className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto origin-top-right bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl py-1 focus:outline-none z-50">
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                    <span className="font-semibold text-gray-900 dark:text-white text-sm">Notificaciones</span>
                    <Link
                      to="/notifications"
                      className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                      onClick={() => refresh()}
                    >
                      Ver todas
                    </Link>
                  </div>
                  {recentNotifications.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
                      No hay notificaciones
                    </p>
                  ) : (
                    recentNotifications.map((n) => {
                      const labelFn = NOTIFICATION_LABELS[n.type];
                      const label = labelFn && n.payload ? labelFn(n.payload) : 'Nueva notificación';
                      const linkTo = (n.type === 'like' || n.type === 'comment') ? '/' : '/services';
                      return (
                        <MenuItem key={n.id}>
                          {({ active }) => (
                            <Link
                              to={linkTo}
                              onClick={() => !n.read && markAsRead(n.id)}
                              className={`flex items-start gap-3 px-4 py-3 text-sm text-left ${
                                active ? 'bg-gray-100 dark:bg-slate-700' : ''
                              } ${!n.read ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}
                            >
                              <span className="text-gray-900 dark:text-white line-clamp-2 flex-1">{label}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                                {formatPostDate(n.createdAt)}
                              </span>
                            </Link>
                          )}
                        </MenuItem>
                      );
                    })
                  )}
                </MenuItems>
              </Menu>
            )}

            {/* Info del Usuario */}
            {currentUser && userData && (
              <>
                {/* Nombre y Email (oculto en móvil) */}
                <div className="hidden sm:flex flex-col items-end">
                  <PremiumName
                    name={userData.displayName || 'Usuario'}
                    isPremium={userData.isPremium}
                    premiumColor={userData.premiumNameColor}
                    nameClassName="text-sm font-medium text-gray-900 dark:text-white"
                    iconSize="xs"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {userData.email}
                  </p>
                </div>
                
                {/* Dropdown del Avatar */}
                <Menu as="div" className="relative">
                  <MenuButton className="flex items-center focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full">
                    <Avatar
                      src={userData.photoURL}
                      alt={userData.displayName}
                      fallbackText={userData.displayName}
                      size="md"
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                    />
                  </MenuButton>

                  <MenuItems className="absolute right-0 mt-2 w-56 origin-top-right bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl py-1 focus:outline-none z-50">
                    {/* Perfil */}
                    <MenuItem>
                      {({ active }) => (
                        <Link
                          to={`/profile/${userData.id}`}
                          className={`flex items-center gap-3 px-4 py-3 text-sm ${
                            active
                              ? 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="font-medium">Perfil</span>
                        </Link>
                      )}
                    </MenuItem>

                    {/* Mis Órdenes */}
                    <MenuItem>
                      {({ active }) => (
                        <Link
                          to="/orders"
                          className={`flex items-center gap-3 px-4 py-3 text-sm ${
                            active
                              ? 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                          <span className="font-medium">Mis Órdenes</span>
                        </Link>
                      )}
                    </MenuItem>

                    {/* Configuración */}
                    <MenuItem>
                      {({ active }) => (
                        <Link
                          to="/settings"
                          className={`flex items-center gap-3 px-4 py-3 text-sm ${
                            active
                              ? 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="font-medium">Configuración</span>
                        </Link>
                      )}
                    </MenuItem>

                    {/* Admin */}
                    {isAdmin && (
                      <MenuItem>
                        {({ active }) => (
                          <Link
                            to="/admin"
                            className={`flex items-center gap-3 px-4 py-3 text-sm ${
                              active
                                ? 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white'
                                : 'text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8c-1.657 0-3 1.343-3 3v2a3 3 0 006 0v-2c0-1.657-1.343-3-3-3z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 12a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                            <span className="font-medium">Admin</span>
                          </Link>
                        )}
                      </MenuItem>
                    )}
                  </MenuItems>
                </Menu>

                {/* Botón de Logout */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="hidden sm:inline-flex"
                >
                  Salir
                </Button>
                
                {/* Botón de Logout móvil (solo icono) */}
                <button
                  onClick={handleLogout}
                  className="
                    sm:hidden p-2.5 rounded-lg 
                    text-gray-600 dark:text-gray-400 
                    hover:bg-gray-100 dark:hover:bg-slate-800 
                    transition-all duration-200
                  "
                  aria-label="Cerrar sesión"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
